import {cn} from '../../lib/utils';
import {Button} from '../Button';
import {Icon} from '../Icon';
import {ListFilterHeader} from '../ListFilterHeader';
import {Popover, PopoverContent, PopoverTrigger} from '../Popover';
import {RadioGroup, RadioGroupItem} from '../RadioGroup';

export interface SortOption {
  value: string;
  label: string;
  defaultDesc: boolean;
}

export interface ListSortingStateItem {
  id: string;
  desc: boolean;
}

export type ListSortingState = ListSortingStateItem[];

export type ListOnSortingChange = (
  updater: ListSortingState | ((prev: ListSortingState) => ListSortingState)
) => void;

export interface ListSortingSelectLabels {
  placeholder: string;
  clear: string;
  toggleAsc: string;
  toggleDesc: string;
}

export interface ListSortingSelectProps {
  options: SortOption[];
  sorting: ListSortingState;
  onSortingChange: ListOnSortingChange;
  labels: ListSortingSelectLabels;
  disabled?: boolean;
  className?: string;
}

const radioRowClass =
  'flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-fg hover:bg-background-surface_hover';

export function ListSortingSelect({
  options,
  sorting,
  onSortingChange,
  labels,
  disabled = false,
  className
}: ListSortingSelectProps) {
  const first = sorting[0];
  const selectedValue = first ? options.find(o => o.value === first.id)?.value : undefined;
  const isDescending = first?.desc ?? true;
  const isSortingApplied = selectedValue !== undefined;

  const triggerText = isSortingApplied
    ? `${labels.placeholder}: ${options.find(o => o.value === selectedValue)?.label ?? ''}`
    : labels.placeholder;

  const handleOptionChange = (value: string) => {
    const opt = options.find(o => o.value === value);
    const desc = value === selectedValue ? isDescending : (opt?.defaultDesc ?? true);
    onSortingChange([{id: value, desc}]);
  };

  const handleDirectionToggle = () => {
    if (selectedValue) {
      onSortingChange([{id: selectedValue, desc: !isDescending}]);
    }
  };

  return (
    <div data-slot="list-sorting-select" className={cn('flex items-center gap-1', className)}>
      <Popover>
        <PopoverTrigger
          render={
            <Button variant="secondary" disabled={disabled} icon={<Icon name="chevrons-updown" />}>
              {triggerText}
            </Button>
          }
        />
        <PopoverContent className="w-56 p-0" align="end">
          <div className="flex flex-col py-2">
            <ListFilterHeader
              title={labels.placeholder}
              clearLabel={labels.clear}
              isActive={isSortingApplied}
              onClearAll={() => onSortingChange([])}
            />
            <RadioGroup value={selectedValue ?? ''} onValueChange={handleOptionChange}>
              {options.map(opt => (
                <label key={opt.value} className={radioRowClass}>
                  <RadioGroupItem value={opt.value} />
                  <span className="min-w-0 flex-1 truncate text-start">{opt.label}</span>
                </label>
              ))}
            </RadioGroup>
          </div>
        </PopoverContent>
      </Popover>
      {isSortingApplied && !disabled && (
        <Button
          variant="tertiary"
          size="icon-sm"
          onClick={handleDirectionToggle}
          aria-label={isDescending ? labels.toggleAsc : labels.toggleDesc}
          title={isDescending ? labels.toggleAsc : labels.toggleDesc}
          icon={<Icon name={isDescending ? 'sort-desc' : 'sort-asc'} />}
        />
      )}
    </div>
  );
}
